import {FC, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  api,
  getMaxAmountElementsInContainer,
  IDocumentInfo,
  IDocumentQueueItem
} from '@kcast-fe/core';
import {Box, Flex} from '@chakra-ui/react';
import _ from 'lodash';

import {DocumentItem, QuickDocumentMenu} from './components';

interface IProps {
  hasThread: boolean;
  queue: Array<IDocumentQueueItem>;
  documentsQueue: Array<IDocumentInfo>;
}

const MAX_WIDTH_PX = 200;

const ContextDocumentsFC: FC<IProps> = ({queue, documentsQueue, hasThread}) => {
  const {data} = api.documents.useContextDocumentsQuery();
  const deleteMutation = api.documents.useDeleteContextDocumentMutation();

  const listRef = useRef<HTMLDivElement | null>(null);

  const [visibleCount, setVisibleCount] = useState(0);
  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const documents = useMemo(() => data?.docs ?? [], [data?.docs]);

  const mergedDocuments = useMemo(() => {
    return _.orderBy([...documents, ...documentsQueue], 'created_at', 'asc');
  }, [documents, documentsQueue]);

  const calculateVisibleCount = useCallback(() => {
    if (mergedDocuments.length === 0) {
      setVisibleCount(0);
    } else {
      const cWidth = listRef.current?.offsetWidth ?? 0;
      const texts = mergedDocuments.map((i) => i.title);
      const amountResult = getMaxAmountElementsInContainer(cWidth, texts, 50, MAX_WIDTH_PX, 32);
      setVisibleCount(amountResult);
    }
  }, [mergedDocuments]);

  useEffect(() => {
    if (!listRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(calculateVisibleCount);
    resizeObserver.observe(listRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateVisibleCount]);

  const count = mergedDocuments.length;
  const invisibleCount = count >= visibleCount ? count - visibleCount : 0;

  const visibleDocuments = mergedDocuments.slice(0, visibleCount);
  const invisibleDocuments = invisibleCount > 0 ? mergedDocuments.slice(-1 * invisibleCount) : [];

  return (
    <Box
      left={'4px'}
      right={'4px'}
      position={'absolute'}
      bottom={`calc(100% + ${hasThread ? '48px' : '4px'})`}
      transition={'bottom var(--tr-150-ei)'}
      transformOrigin={'bottom center'}
      zIndex={1}
    >
      <Flex ref={listRef} gap={'4px'} alignItems={'center'}>
        {/* DOCUMENT LIST */}
        {visibleDocuments.map((doc) => {
          const queueItem = queue.find((q) => q.id === doc.id);
          const progress = queueItem?.progress ?? 0;

          const isUploading = documentsQueue.some((q) => q.id === doc.id) && progress !== 1;
          const isProcessing = !queueItem?.isDone && progress === 1;

          return (
            <DocumentItem
              key={doc.id}
              document={doc}
              progress={progress}
              maxWidthPx={MAX_WIDTH_PX}
              isUploading={isUploading}
              isProcessing={isProcessing}
              isDeleting={deletingIds.some((i) => i === doc.id)}
              onDelete={() => {
                setDeletingIds((prev) => [...prev, doc.id]);
                deleteMutation.mutate(doc.id);
              }}
            />
          );
        })}

        {/* SHOW MORE BUTTON */}
        {invisibleDocuments.length > 0 && (
          <QuickDocumentMenu
            container={listRef}
            items={invisibleDocuments.map((doc) => {
              const queueItem = queue.find((q) => q.id === doc.id);
              const progress = queueItem?.progress ?? 0;

              const isUploading = documentsQueue.some((q) => q.id === doc.id) && progress !== 1;
              const isProcessing = !queueItem?.isDone && progress === 1;

              return {
                id: doc.id,
                node: (
                  <DocumentItem
                    key={doc.id}
                    document={doc}
                    progress={progress}
                    maxWidthPx={MAX_WIDTH_PX}
                    isUploading={isUploading}
                    isProcessing={isProcessing}
                    isDeleting={deletingIds.some((i) => i === doc.id)}
                    onDelete={() => {
                      setDeletingIds((prev) => [...prev, doc.id]);
                      deleteMutation.mutate(doc.id);
                    }}
                  />
                )
              };
            })}
          />
        )}
      </Flex>
    </Box>
  );
};

export const ContextDocuments = memo(ContextDocumentsFC);
